import { OrderReviewCompletion } from './OrderReviewCompletion';
import React, { useEffect, useRef, useState } from 'react';
import { usePageRefresh } from '../api/use-page-refresh';
import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  FileText,
  History,
  RefreshCw,
} from 'lucide-react';
import {
  Link,
  useLoaderData,
  useRevalidator,
  useRouteLoaderData,
  useSearchParams,
} from 'react-router-dom';
import { z } from 'zod';
import {
  REVIEW_CATEGORIES,
  REVIEW_AREA_LABELS,
  reviewNarrative,
  type Me,
  type ProjectDetail,
} from '@tinypro-audit/contracts';
import { ReviewCategoryBadge } from '../components/ReviewCategoryBadge';
import { ProjectPreSiteReports } from '../components/PreSiteReportLink';
import {
  inboxReturnPath,
  isPreSiteReviewField,
  topicMatchesReviewLink,
} from './review-navigation';
import { ProjectPreSiteReview } from './ProjectPreSiteReview';
import { fetchJson } from '../api/client';
import { Evidence, ProjectEvidenceProvider } from './ProjectEvidence';
import { ComparisonChecks, HoldEditor } from './WorkflowReviewPanel';
import {
  DetailedChecks,
  LegacyPurchasingReview,
  ProjectDocuments,
  ProjectHistory,
  Specifications,
  TaskRecords,
  reviewDate,
} from './ProjectReviewSupport';
import {
  columnEvidence,
  comparisonValues,
  fieldLabel,
  openingLabel,
  projectReviewModel,
  readable,
  topicDescription,
  topicCategory,
  topicEvidence,
  topicScope,
  type EvidenceColumn,
  type ReviewTopic,
} from './project-review-model';
import { collectionColor, collectionLabels } from './ProjectAuditsPage';
import './ProjectAuditDetailPage.css';

const countLabel = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

function SourceColumn({
  topic,
  data,
  column,
  title,
}: {
  topic: ReviewTopic;
  data: ProjectDetail;
  column: EvidenceColumn;
  title: string;
}) {
  const values = comparisonValues(topic.allChecks, column);
  const citations = columnEvidence(topic, data, column);
  const value = (item: (typeof values)[number], i: number) => (
    <div key={i}>
      <Text fw={600} size="sm">
        {item.value}
      </Text>
      {!!item.scopes.length && (
        <Text size="xs" c="dimmed">
          {item.scopes
            .map((ref) => openingLabel(data, ref, topic.order.submission_id))
            .join(' · ')}
        </Text>
      )}
    </div>
  );
  return (
    <section className="project-evidence-column" aria-label={title}>
      <Text className="project-eyebrow" c="dimmed" mb="sm">
        {title}
      </Text>
      <Stack gap="sm">
        {!values.length && (
          <Text size="sm" c="dimmed">
            No value established in this review.
          </Text>
        )}
        {values.slice(0, 2).map(value)}
        {values.length > 2 && (
          <details>
            <summary>{values.length - 2} more recorded values</summary>
            <Stack gap="sm" mt="sm">
              {values.slice(2).map(value)}
            </Stack>
          </details>
        )}
        <Evidence items={citations.slice(0, 2)} />
        {citations.length > 2 && (
          <details>
            <summary>{citations.length - 2} more citations</summary>
            <Evidence items={citations.slice(2)} />
          </details>
        )}
        {!!values.length && !citations.length && (
          <Text size="xs" c="dimmed">
            See all supporting evidence below for source references.
          </Text>
        )}
      </Stack>
    </section>
  );
}

function TopicReview({
  topic,
  data,
  active = true,
}: {
  topic: ReviewTopic;
  data: ProjectDetail;
  active?: boolean;
}) {
  const actions = topic.issue
    ? [topic.issue.next_action]
    : [...new Set(topic.checks.filter((c) => c.action).map((c) => c.action))];
  return (
    <Paper
      withBorder
      p="lg"
      radius="md"
      className="project-topic-detail"
      id={
        active
          ? 'project-topic-detail'
          : `project-closed-topic-${encodeURIComponent(topic.key)}`
      }
      tabIndex={-1}
      aria-label={`${topic.title} review`}
    >
      <Stack gap="lg">
        <div>
          <Group gap="sm" mb="xs">
            <ReviewCategoryBadge category={topicCategory(topic)} />
            <Text size="sm" c="dimmed">
              PO {topic.order.identity.po_number ?? topic.order.order_ref}
            </Text>
          </Group>
          <Title order={3}>{topic.title}</Title>
          <Text size="sm" c="dimmed" mt={4}>
            {topicScope(topic, data)}
          </Text>
          <Text mt="md" className="project-review-description">
            {reviewNarrative(topicDescription(topic, data))}
          </Text>
        </div>
        {topic.issue && (
          <Group gap="xs" aria-label="Related comparison fields">
            {[...new Set(topic.checks.map((check) => check.field))].map(
              (field) => (
                <Badge key={field} variant="light" color="gray" tt="none">
                  {fieldLabel(field)}
                </Badge>
              ),
            )}
          </Group>
        )}
        {topic.issue ? (
          <div className="project-issue-documents">
            {topic.issue.document_context.map((source) => {
              const citations = topic.issue!.evidence.filter(
                (e) => e.kind === 'document' && e.id === source.document_id,
              );
              const labels = {
                approved_purchase: 'Signed purchase & approved changes',
                revision_approval_unverified:
                  'Revision · approval not established',
                submitted_po: 'Submitted PO',
                acknowledgement: 'Manufacturer acknowledgement',
                supporting: 'Supporting document',
              };
              return (
                <Paper
                  withBorder
                  radius="md"
                  p="md"
                  key={source.document_id}
                  component="section"
                  aria-label={labels[source.role]}
                >
                  <Text
                    className="project-eyebrow"
                    c={
                      source.role === 'revision_approval_unverified'
                        ? 'orange.8'
                        : 'dimmed'
                    }
                    mb="sm"
                  >
                    {labels[source.role]}
                  </Text>
                  <Text size="sm" mb="sm">
                    {reviewNarrative(source.summary)}
                  </Text>
                  <Evidence items={citations.slice(0, 2)} />
                  {citations.length > 2 && (
                    <details>
                      <summary>More citations from this document</summary>
                      <Evidence items={citations.slice(2)} />
                    </details>
                  )}
                </Paper>
              );
            })}
            {!topic.issue.document_context.length && (
              <Evidence items={topic.issue.evidence} />
            )}
          </div>
        ) : (
          <div className="project-evidence-grid">
            <SourceColumn
              topic={topic}
              data={data}
              column="purchase"
              title="Approved estimate & changes"
            />
            <SourceColumn
              topic={topic}
              data={data}
              column="po"
              title="Submitted PO"
            />
            <SourceColumn
              topic={topic}
              data={data}
              column="acknowledgement"
              title="Manufacturer acknowledgement"
            />
          </div>
        )}
        {!!actions.length && (
          <div className="project-next-action">
            <Text fw={600} size="sm" mb="xs">
              Next action
            </Text>
            <Text size="sm">{reviewNarrative(actions[0])}</Text>
            {actions.length > 1 && (
              <details>
                <summary>Additional instructions for this topic</summary>
                <Stack gap="sm" mt="sm">
                  {actions.slice(1).map((action, i) => (
                    <Text size="sm" key={i}>
                      {reviewNarrative(action)}
                    </Text>
                  ))}
                </Stack>
              </details>
            )}
          </div>
        )}
        <TaskRecords
          tasks={topic.tasks}
          data={data}
          issueScope={
            topic.issue
              ? { orderId: topic.order.id, title: topic.title }
              : undefined
          }
        />
        <Accordion multiple>
          <Accordion.Item value="evidence">
            <Accordion.Control>
              All supporting evidence and comparison details
            </Accordion.Control>
            <Accordion.Panel>
              <Stack>
                <Evidence items={topicEvidence(topic)} />
                <ComparisonChecks checks={topic.allChecks} expanded={false} />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Stack>
    </Paper>
  );
}

export function ProjectAuditDetailPage(): React.ReactElement {
  const data = useLoaderData() as ProjectDetail;
  const [params] = useSearchParams();
  // A route parameter change must reset selected topics, tabs and document previews.
  return (
    <ProjectReview
      key={`${data.project.id}:${params.toString()}`}
      data={data}
    />
  );
}

function ProjectReview({ data }: { data: ProjectDetail }) {
  const { me } = useRouteLoaderData('root') as { me: Me };
  const revalidator = useRevalidator();
  const [params] = useSearchParams();
  const returnTo = inboxReturnPath(params);
  const model = projectReviewModel(data);
  const preSiteRequested =
    !params.has('exception') && isPreSiteReviewField(params.get('field'));
  const topicRequested =
    params.has('exception') || Boolean(params.get('field'));
  const linkedTopic = model.topics.find((topic) =>
    topicMatchesReviewLink(topic, params),
  );
  const linkedClosedTopic = linkedTopic
    ? undefined
    : model.closedTopics.find((topic) => topicMatchesReviewLink(topic, params));
  const [preSiteExpanded, setPreSiteExpanded] = useState(preSiteRequested);
  const [tab, setTab] = useState<string | null>(
    ['documents', 'history'].includes(params.get('tab') ?? '')
      ? params.get('tab')
      : 'review',
  );
  const [topicKey, setTopicKey] = useState<string | null>(
    linkedTopic?.key ?? null,
  );
  const [reviewSections, setReviewSections] = useState<string[]>(
    linkedClosedTopic ? ['reviewed-topics'] : [],
  );
  const [orderId, setOrderId] = useState<string | null>(
    () =>
      data.workflow.orders.find(
        (item) => item.order_ref === params.get('order'),
      )?.id ?? null,
  );
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const active = data.runs.find(
    (r) =>
      r.submission_id === data.project.latest_submission_id &&
      ['queued', 'running', 'retry_wait'].includes(r.state),
  );
  const failed =
    model.latestAttempt?.state === 'failed' ? model.latestAttempt : undefined;
  const selectedOrderId = model.orders.some((o) => o.id === orderId)
    ? orderId
    : null;
  const topics = model.topics.filter(
    (topic) => !selectedOrderId || topic.order.id === selectedOrderId,
  );
  const selected =
    topics.find((topic) => topic.key === topicKey) ??
    topics.find((topic) => topic.key === linkedTopic?.key) ??
    (params.has('exception') && !linkedTopic ? undefined : topics[0]);
  const requestedTopicMissing = Boolean(
    topicRequested &&
    !preSiteRequested &&
    tab === 'review' &&
    model.run &&
    !linkedTopic &&
    !linkedClosedTopic,
  );
  const positioned = useRef(false);
  useEffect(() => {
    const targetId = preSiteRequested
      ? 'project-presite-review'
      : requestedTopicMissing
        ? 'project-review-link-missing'
        : linkedClosedTopic
          ? `project-closed-topic-${encodeURIComponent(linkedClosedTopic.key)}`
          : 'project-topic-detail';
    if (
      positioned.current ||
      (!preSiteRequested &&
        (tab !== 'review' ||
          !topicRequested ||
          (!requestedTopicMissing &&
            !linkedClosedTopic &&
            (!selected || !topicMatchesReviewLink(selected, params)))))
    )
      return;
    if (linkedClosedTopic && !reviewSections.includes('reviewed-topics')) {
      setReviewSections((sections) => [...sections, 'reviewed-topics']);
      return;
    }
    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(targetId);
      if (target) {
        positioned.current = true;
        target.focus({ preventScroll: true });
        target.scrollIntoView({ block: 'start' });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [
    params,
    preSiteRequested,
    selected?.key,
    tab,
    topicRequested,
    requestedTopicMissing,
    linkedClosedTopic?.key,
    reviewSections,
  ]);
  const allVerified =
    model.current &&
    model.orders.length > 0 &&
    model.orders.every((o) => o.result!.verification === 'verified');
  const awaitingDocuments = model.orders.flatMap((order) =>
    order
      .result!.stages.filter((stage) => stage.state === 'awaiting_documents')
      .map((stage) => ({ order, stage })),
  );
  const subtitle = model.conflicts.length
    ? `${countLabel(model.conflicts.length, 'purchasing document conflict')} to resolve`
    : model.clarifications.length
      ? `${countLabel(model.clarifications.length, 'purchasing topic')} need${model.clarifications.length === 1 ? 's' : ''} clarification`
      : awaitingDocuments.length
        ? 'Waiting on purchasing documents'
        : model.followUps.length
          ? 'Evidence reviewed · task updates remain'
          : allVerified
            ? 'Purchasing evidence verified'
            : model.orders.length
              ? 'No purchasing issues flagged'
              : model.run
                ? 'Review the purchasing summary below'
                : 'Waiting for an ordering review';
  usePageRefresh(data.refresh, data.project.st_project_id);
  const retry = async () => {
    setBusy(true);
    setError('');
    try {
      await fetchJson(
        `/api/projects/${data.project.st_project_id}/retry`,
        { method: 'POST', body: '{}' },
        z.unknown(),
      );
      await revalidator.revalidate();
    } catch (error) {
      setError(
        error instanceof Error ? error.message : 'Could not start the recheck.',
      );
    } finally {
      setBusy(false);
    }
  };
  const reviewTopIssue = () => {
    setOrderId(null);
    setTopicKey(model.topics[0]?.key ?? null);
    setTab('review');
    window.requestAnimationFrame(() =>
      document
        .getElementById('project-topic-detail')
        ?.scrollIntoView({ block: 'start' }),
    );
  };
  const showAssessment = (id: string) => {
    setAssessmentId(id);
    setTab('documents');
    window.requestAnimationFrame(() =>
      document
        .getElementById(`assessment-${id}`)
        ?.scrollIntoView({ block: 'start' }),
    );
  };
  return (
    <ProjectEvidenceProvider data={data} onAssessment={showAssessment}>
      <Stack gap="lg" className="project-review-page">
        <Anchor component={Link} to={returnTo ?? '/projects'} size="sm">
          ← {returnTo ? 'Back to Inbox' : 'Projects'}
        </Anchor>
        <Group justify="space-between" align="start">
          <div>
            <Title order={2}>Project {data.project.display_number}</Title>
            <Text size="sm" c="dimmed">
              Ordering review
              {data.project.display_number !== data.project.st_project_id
                ? ` · ServiceTitan ID ${data.project.st_project_id}`
                : ''}
            </Text>
          </div>
          <Group gap="xs">
            <ProjectPreSiteReports data={data} />
            {me.role === 'admin' && (
              <Stack gap={4} align="flex-end">
                <Button
                  variant="default"
                  size="sm"
                  leftSection={<RefreshCw size={15} />}
                  disabled={
                    Boolean(active) || !data.project.latest_submission_id
                  }
                  loading={busy}
                  onClick={() => void retry()}
                >
                  Re-run analysis
                </Button>
                <Text size="xs" c="dimmed">
                  Uses the documents already uploaded.
                </Text>
              </Stack>
            )}
          </Group>
        </Group>
        {error && <Alert color="red">{error}</Alert>}
        {requestedTopicMissing && (
          <Alert
            id="project-review-link-missing"
            tabIndex={-1}
            color="blue"
            title="This linked item is not available in the latest review"
          >
            Review the project history and task records for the original finding
            and its latest disposition.
            <Button
              variant="subtle"
              size="compact-sm"
              ml="sm"
              onClick={() => setTab('history')}
            >
              Open history
            </Button>
          </Alert>
        )}
        {data.collection && (
          <Alert
            color={collectionColor(data.collection.state)}
            title={
              data.collection.state === 'selected'
                ? data.project.latest_submission_id
                  ? 'Waiting for collector to refresh documents'
                  : 'Waiting for document collection'
                : collectionLabels[data.collection.state]
            }
          >
            <Text size="sm">
              {data.collection.note ||
                'The collector is preparing project evidence.'}
            </Text>
            <Text size="xs" mt={4}>
              Last update: {reviewDate(data.collection.updated_at)}
              {data.collection.document_count !== null
                ? ` · ${data.collection.document_count} documents declared`
                : ''}
              .{' '}
              {data.project.latest_submission_id
                ? 'The review below covers the last accepted evidence.'
                : 'Analysis begins after an upload is accepted.'}
            </Text>
            {data.collection.state === 'paused' && (
              <Text size="sm">
                No collector update in the last hour. Resume collection to
                continue.
              </Text>
            )}
          </Alert>
        )}
        {active && (
          <Alert
            color="blue"
            title={
              active.state === 'running'
                ? 'Analysis in progress'
                : active.state === 'retry_wait'
                  ? 'Analysis waiting to retry'
                  : 'Analysis queued'
            }
          >
            {typeof active.progress.message === 'string'
              ? active.progress.message
              : 'The latest evidence is waiting for analysis.'}
          </Alert>
        )}
        {failed && (
          <Alert color="red" title="The latest analysis did not finish">
            {typeof failed.error?.message === 'string'
              ? failed.error.message
              : 'Open History for the error details.'}{' '}
            A recheck uses the already uploaded evidence.
          </Alert>
        )}
        {model.run && !model.current && (
          <Alert color="yellow" title="Previous review · not current">
            The latest evidence, recheck or policy has not completed review.
            Findings below are from {reviewDate(model.run.completed_at)} and
            cannot verify the current order.
          </Alert>
        )}

        <Paper withBorder radius="md" p="lg" className="project-overview">
          <Group
            justify="space-between"
            align="start"
            gap="lg"
            className="project-overview-header"
          >
            <div className="project-overview-copy">
              <Group gap="xs" mb="sm">
                <Text className="project-eyebrow" c="dimmed">
                  {model.current
                    ? 'Latest completed purchasing review'
                    : model.run
                      ? 'Previous completed purchasing review'
                      : 'Review progress'}
                </Text>
                {allVerified && (
                  <CheckCircle2 size={16} color="var(--mantine-color-teal-7)" />
                )}
              </Group>
              <Title order={3} className="project-overview-title">
                {subtitle}
              </Title>
              {!!model.orders.length && (
                <Text size="sm" mt="xs" c="dimmed">
                  {countLabel(model.orders.length, 'order')} ·{' '}
                  {countLabel(
                    new Set(
                      model.orders.flatMap((o) => o.identity.opening_refs),
                    ).size,
                    'opening',
                  )}
                  {model.orders.length === 1
                    ? ` · ${model.orders[0].identity.manufacturer ?? 'Manufacturer unconfirmed'} · PO ${model.orders[0].identity.po_number ?? model.orders[0].order_ref}${model.orders[0].identity.manufacturer_order_number ? ` · SO ${model.orders[0].identity.manufacturer_order_number}` : ''}`
                    : ''}
                </Text>
              )}
              {model.run && (
                <Text size="xs" c="dimmed" mt={4}>
                  Reviewed {reviewDate(model.run.completed_at)} · Based on{' '}
                  {model.current ? 'latest accepted' : 'previous'} evidence
                </Text>
              )}
            </div>
            {!!model.topics.length && (
              <Button
                onClick={reviewTopIssue}
                rightSection={<ArrowRight size={16} />}
              >
                Review top issue
              </Button>
            )}
          </Group>
          {!!model.orders.length && (
            <div className="project-review-counts">
              <span>
                <strong>{model.conflicts.length}</strong> document conflict
                {model.conflicts.length === 1 ? '' : 's'}
              </span>
              <span>
                <strong>{model.clarifications.length}</strong> clarification
                {model.clarifications.length === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </Paper>

        {model.current && model.orders.length > 0 && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Human order reviews:{' '}
              {
                model.orders.filter(
                  (o) => o.review_completion?.action === 'complete',
                ).length
              }{' '}
              of {model.orders.length} complete.
              {model.orders.every(
                (o) => o.review_completion?.action === 'complete',
              )
                ? ' Project review complete.'
                : ''}
            </Text>
            <Button
              size="compact-sm"
              variant="subtle"
              onClick={() => {
                setTab('review');
                setReviewSections((v) => [...new Set([...v, 'orders'])]);
                window.setTimeout(
                  () =>
                    document
                      .getElementById('project-order-completion')
                      ?.scrollIntoView({ block: 'start' }),
                  50,
                );
              }}
            >
              Review orders
            </Button>
          </Group>
        )}
        <ProjectPreSiteReview
          data={data}
          model={model}
          expanded={preSiteExpanded}
          onExpandedChange={setPreSiteExpanded}
          requestedOrder={params.get('order')}
          onHistory={() => {
            setTab('history');
            window.requestAnimationFrame(() =>
              document
                .getElementById('project-review-tabs')
                ?.scrollIntoView({ block: 'start' }),
            );
          }}
        />

        <Tabs
          id="project-review-tabs"
          value={tab}
          onChange={setTab}
          keepMounted={false}
        >
          <Tabs.List mb="lg">
            <Tabs.Tab value="review" leftSection={<ClipboardCheck size={16} />}>
              Review
            </Tabs.Tab>
            <Tabs.Tab value="documents" leftSection={<FileText size={16} />}>
              Documents{' '}
              <span className="project-tab-count">{data.documents.length}</span>
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<History size={16} />}>
              History
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="review">
            <Stack gap="lg">
              {!!awaitingDocuments.length && (
                <Alert
                  color="yellow"
                  title="Required documents are still missing"
                >
                  <Stack gap="xs">
                    {awaitingDocuments.map(({ order, stage }) => (
                      <Text size="sm" key={`${order.id}:${stage.stage}`}>
                        PO {order.identity.po_number ?? order.order_ref} ·{' '}
                        {REVIEW_AREA_LABELS[stage.stage]}:{' '}
                        {reviewNarrative(stage.summary)}
                      </Text>
                    ))}
                    <Anchor
                      component="button"
                      size="sm"
                      onClick={() => setTab('documents')}
                      ta="left"
                    >
                      View collection gaps and available documents
                    </Anchor>
                  </Stack>
                </Alert>
              )}
              {model.orders.length > 1 && (
                <Select
                  label="Order"
                  placeholder="All orders"
                  clearable
                  value={selectedOrderId}
                  onChange={setOrderId}
                  data={model.orders.map((order) => ({
                    value: order.id,
                    label: `PO ${order.identity.po_number ?? order.order_ref} · ${order.identity.manufacturer ?? 'Manufacturer unconfirmed'}`,
                  }))}
                />
              )}
              {!!topics.length && (
                <div className="project-issue-layout">
                  <nav
                    className="project-topic-navigation"
                    aria-label="Purchasing review topics"
                  >
                    <Text className="project-eyebrow" c="dimmed" mb="sm">
                      Purchasing{' '}
                      {topics.some((topic) => topic.issue)
                        ? 'issues'
                        : 'topics'}{' '}
                      · {topics.length}
                    </Text>
                    <div className="project-topic-buttons">
                      {topics.map((topic) => (
                        <button
                          type="button"
                          className="project-topic-button"
                          aria-pressed={selected?.key === topic.key}
                          aria-controls="project-topic-detail"
                          key={topic.key}
                          onClick={() => setTopicKey(topic.key)}
                        >
                          <span className="project-topic-heading">
                            <span
                              className={`project-topic-dot project-topic-dot-${topic.kind}`}
                            />
                            <span>{topic.title}</span>
                            <ChevronRight size={15} aria-hidden />
                          </span>
                          <span className="project-topic-meta">
                            {REVIEW_CATEGORIES[topicCategory(topic)].label}
                          </span>
                          <span
                            className="project-topic-scope"
                            title={topicScope(topic, data)}
                          >
                            {topicScope(topic, data)}
                            {model.orders.length > 1
                              ? ` · PO ${topic.order.identity.po_number ?? topic.order.order_ref}`
                              : ''}
                          </span>
                        </button>
                      ))}
                    </div>
                    <Text size="xs" c="dimmed" mt="sm">
                      Related comparisons are grouped by{' '}
                      {topics.some((topic) => topic.issue)
                        ? 'the decision needed'
                        : 'topic'}
                      . Review decisions preserve the original findings.
                    </Text>
                  </nav>
                  {selected && (
                    <TopicReview
                      key={selected.key}
                      topic={selected}
                      data={data}
                    />
                  )}
                </div>
              )}
              {!topics.length && model.orders.length > 0 && (
                <Alert
                  color={allVerified ? 'teal' : 'blue'}
                  icon={
                    allVerified ? (
                      <CheckCircle2 size={18} />
                    ) : (
                      <AlertCircle size={18} />
                    )
                  }
                  title="No active purchasing topics in this view"
                >
                  {allVerified
                    ? 'The current analysis marked the orders verified. Human task records and manufacturer hold status remain separate.'
                    : 'Pre-site review status is shown separately above. Check the order summaries and required documents for purchasing details.'}
                </Alert>
              )}
              {!model.orders.length &&
                model.run?.result &&
                !model.run.result.workflow && (
                  <LegacyPurchasingReview data={data} />
                )}
              {!model.orders.length &&
                (!model.run?.result || model.run.result.workflow) && (
                  <Paper withBorder radius="md" p="xl">
                    <Stack gap="sm">
                      <Title order={3}>
                        {data.project.latest_submission_id
                          ? 'Order review not available yet'
                          : 'Evidence collection is underway'}
                      </Title>
                      <Text c="dimmed">
                        {data.project.latest_submission_id
                          ? 'Documents and collection gaps are available now. Order issues appear when analysis completes.'
                          : 'This project was selected by the collector. Uploaded documents and ordering results will appear here as they become available.'}
                      </Text>
                      <Button
                        variant="light"
                        onClick={() => setTab('documents')}
                        w="fit-content"
                      >
                        View available evidence
                      </Button>
                    </Stack>
                  </Paper>
                )}
              {!!model.followUps.length && (
                <Accordion variant="separated" radius="md">
                  <Accordion.Item value="followups">
                    <Accordion.Control>
                      Human follow-ups ·{' '}
                      {countLabel(model.followUps.length, 'task')}
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Text size="sm" mb="md">
                        These items still need a review decision or a verified
                        correction.
                      </Text>
                      <TaskRecords tasks={model.followUps} data={data} />
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              )}
              {!!model.orders.length && (
                <Accordion
                  multiple
                  variant="separated"
                  radius="md"
                  value={reviewSections}
                  onChange={setReviewSections}
                >
                  <Accordion.Item value="matches">
                    <Accordion.Control>
                      Matching comparisons · {model.matches.length}
                    </Accordion.Control>
                    <Accordion.Panel>
                      <DetailedChecks orders={model.orders} mode="matched" />
                    </Accordion.Panel>
                  </Accordion.Item>
                  {!!model.notes.length && (
                    <Accordion.Item value="notes">
                      <Accordion.Control>
                        Informational notes · {model.notes.length}
                      </Accordion.Control>
                      <Accordion.Panel>
                        <DetailedChecks
                          orders={model.orders}
                          mode="information"
                        />
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}
                  {!!model.closedTopics.length && (
                    <Accordion.Item value="reviewed-topics">
                      <Accordion.Control>
                        Closed purchasing topics · {model.closedTopics.length}
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack>
                          {model.closedTopics.map((topic) => (
                            <TopicReview
                              key={topic.key}
                              topic={topic}
                              data={data}
                              active={false}
                            />
                          ))}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}
                  <Accordion.Item value="orders" id="project-order-completion">
                    <Accordion.Control>
                      Order completion and manufacturer holds
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="xl">
                        {model.orders.map((order) => (
                          <section key={order.id}>
                            <Group mb="sm">
                              <Title order={4}>
                                PO {order.identity.po_number ?? order.order_ref}
                              </Title>
                              <Badge
                                color={
                                  model.current &&
                                  order.result!.verification === 'verified'
                                    ? 'teal'
                                    : 'gray'
                                }
                              >
                                {model.current
                                  ? fieldLabel(order.result!.verification)
                                  : 'Previous review'}
                              </Badge>
                            </Group>
                            <Text size="sm" c="dimmed">
                              {order.identity.manufacturer} · Manufacturer order{' '}
                              {order.identity.manufacturer_order_number ??
                                'Not established'}
                            </Text>
                            <Stack gap="md" mt="md">
                              {order.result!.stages.map((stage) => (
                                <div key={stage.stage}>
                                  <Text fw={600} size="sm">
                                    {REVIEW_AREA_LABELS[stage.stage]} ·{' '}
                                    {readable(stage.state)}
                                  </Text>
                                  <Text size="sm">
                                    {reviewNarrative(stage.summary)}
                                  </Text>
                                </div>
                              ))}
                              <OrderReviewCompletion
                                data={data}
                                order={order}
                              />
                              <HoldEditor data={data} order={order} />
                            </Stack>
                          </section>
                        ))}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                  <Accordion.Item value="all">
                    <Accordion.Control>
                      All comparison details and extracted specifications
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack>
                        <DetailedChecks orders={model.orders} mode="all" />
                        <Specifications data={data} runId={model.run?.id} />
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              )}
            </Stack>
          </Tabs.Panel>
          <Tabs.Panel value="documents">
            <ProjectDocuments data={data} assessmentId={assessmentId} />
          </Tabs.Panel>
          <Tabs.Panel value="history">
            <ProjectHistory data={data} />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </ProjectEvidenceProvider>
  );
}
